import type { TFunction } from 'i18next';

import { appStoreUrl, googlePlayUrl } from './env';

/**
 * JSON-LD structured data for the marketing site. Emitted by `SeoHead` into a
 * `<script type="application/ld+json">` during SSG so search engines and AI
 * answer engines (Google AI Overviews, ChatGPT, Perplexity, …) can read a
 * machine-readable description of the product and its FAQ without executing JS.
 *
 * Keep the canonical origin in sync with `CANONICAL_ORIGIN` in seo-head.tsx.
 */
const ORIGIN = 'https://workingclock.app';

const ORG_ID = `${ORIGIN}/#organization`;
const SITE_ID = `${ORIGIN}/#website`;

/**
 * The always-on graph: the organization, the website, and the product itself
 * (a cross-platform SoftwareApplication with a free tier). Rendered on every
 * page so any indexed URL carries the core entity description.
 */
export function baseGraph(): Record<string, unknown>[] {
  return [
    {
      '@type': 'Organization',
      '@id': ORG_ID,
      name: 'WorkingClock',
      url: ORIGIN,
      logo: `${ORIGIN}/icon-512.png`,
      description:
        'WorkingClock is a cross-platform time tracker for freelancers and teams, with one account shared across web, mobile, and desktop.',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        url: `${ORIGIN}/contact`,
      },
    },
    {
      '@type': 'WebSite',
      '@id': SITE_ID,
      name: 'WorkingClock',
      url: ORIGIN,
      publisher: { '@id': ORG_ID },
      inLanguage: 'en',
    },
    {
      '@type': 'SoftwareApplication',
      name: 'WorkingClock',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web, iOS, Android, Windows, macOS, Linux',
      url: ORIGIN,
      installUrl: [appStoreUrl, googlePlayUrl],
      downloadUrl: `${ORIGIN}/download`,
      image: `${ORIGIN}/og-image.png`,
      description:
        'A fast, focused time tracker for freelancers and teams: start a timer on any device, track projects, tasks, tags, and clients, then export reports as CSV or branded PDF.',
      publisher: { '@id': ORG_ID },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'EUR',
        description: 'Free plan — unlimited time tracking, projects, clients, tags, and CSV export.',
      },
    },
  ];
}

/**
 * FAQPage schema built from the same `marketing` i18n keys the FAQ page
 * renders (see faq-page.tsx), so the structured answers never drift from the
 * visible copy. Composed answers (pre/link/post) are joined into plain text.
 */
export function faqGraph(t: TFunction): Record<string, unknown> {
  const items: Array<{ base: string; answer: string[] }> = [
    { base: 'general.whatIs', answer: ['general.whatIs.answer'] },
    { base: 'general.installNeeded', answer: ['general.installNeeded.answer'] },
    { base: 'general.worksAcrossDevices', answer: ['general.worksAcrossDevices.answer'] },
    {
      base: 'pricingAndPlans.planDifferences',
      answer: [
        'pricingAndPlans.planDifferences.answerPre',
        'pricingAndPlans.planDifferences.answerLink',
        'pricingAndPlans.planDifferences.answerPost',
      ],
    },
    { base: 'pricingAndPlans.csvFree', answer: ['pricingAndPlans.csvFree.answer'] },
    { base: 'pricingAndPlans.cancelSubscription', answer: ['pricingAndPlans.cancelSubscription.answer'] },
    {
      base: 'dataAndPrivacy.whereStored',
      answer: [
        'dataAndPrivacy.whereStored.answerPre',
        'dataAndPrivacy.whereStored.answerLink',
        'dataAndPrivacy.whereStored.answerPost',
      ],
    },
    { base: 'dataAndPrivacy.exportOrDelete', answer: ['dataAndPrivacy.exportOrDelete.answer'] },
    { base: 'dataAndPrivacy.whoCanSee', answer: ['dataAndPrivacy.whoCanSee.answer'] },
    { base: 'importingData.canImport', answer: ['importingData.canImport.answer'] },
  ];

  return {
    '@type': 'FAQPage',
    '@id': `${ORIGIN}/help/faq#faqpage`,
    mainEntity: items.map(({ base, answer }) => ({
      '@type': 'Question',
      name: t(`help.faq.${base}.question`),
      acceptedAnswer: {
        '@type': 'Answer',
        text: answer
          .map((k) => t(`help.faq.${k}`))
          .join(' ')
          .replace(/\s+([,.])/g, '$1')
          .replace(/\s{2,}/g, ' ')
          .trim(),
      },
    })),
  };
}

/** Wraps a graph node list in a JSON-LD document string. */
export function jsonLd(graph: Record<string, unknown>[]): string {
  return JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
}
